import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Plus, Trash2, Sparkles, AlertTriangle, Film, Rocket, X,
  Baby, Star, HeartHandshake, BookOpen, Calculator, HelpCircle, Check, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';
import { custodyFor, currentCycleDays, nextTransition, PARENT_LABEL } from './custody';

const LOG_ICONS = {
  small_win: { icon: Star, tone: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10' },
  struggle: { icon: AlertTriangle, tone: 'text-amber-300 border-amber-400/30 bg-amber-500/10' },
  movie_watched: { icon: Film, tone: 'text-violet-300 border-violet-400/30 bg-violet-500/10' },
  mission_done: { icon: Rocket, tone: 'text-sky-300 border-sky-400/30 bg-sky-500/10' },
  hebrew_session: { icon: BookOpen, tone: 'text-teal-300 border-teal-400/30 bg-teal-500/10' },
  math_session: { icon: Calculator, tone: 'text-teal-300 border-teal-400/30 bg-teal-500/10' },
  responsibility_done: { icon: Check, tone: 'text-lime-300 border-lime-400/30 bg-lime-500/10' },
  social_moment: { icon: HeartHandshake, tone: 'text-rose-300 border-rose-400/30 bg-rose-500/10' },
  note: { icon: HelpCircle, tone: 'text-white/70 border-white/15 bg-white/5' },
};

const FOCUS_ICONS = {
  hebrew: BookOpen, math: Calculator, social: HeartHandshake, responsibility: Check,
};

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.round(d / 60)}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
};

// ---------- Custody strip (14-day rolling schedule) ----------

const CustodyRow = ({ label, row, todayKey }) => (
  <div>
    <div className="text-[9px] tracking-[0.28em] uppercase text-white/30 font-mono mb-1.5 px-1">{label}</div>
    <div className="grid grid-cols-7 gap-1.5">
      {row.map(({ date, parent }) => {
        const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        const isToday = key === todayKey;
        const mine = parent === 'me';
        return (
          <div
            key={key}
            data-testid={`nexa-custody-day-${date.toISOString().slice(0, 10)}`}
            className={`relative aspect-[3/4] rounded-lg border flex flex-col items-center justify-center gap-0.5 ${
              mine
                ? 'border-sky-400/45 bg-sky-400/[0.1] text-sky-100'
                : 'border-white/12 bg-white/[0.03] text-white/60'
            } ${isToday ? 'ring-1 ring-white/70' : ''}`}
          >
            <div className="text-[8.5px] font-mono tracking-widest uppercase opacity-70">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
            </div>
            <div className="text-base font-medium leading-none tabular-nums">{date.getDate()}</div>
            <div className={`w-1 h-1 rounded-full mt-0.5 ${mine ? 'bg-sky-300' : 'bg-white/40'}`} />
          </div>
        );
      })}
    </div>
  </div>
);

const CustodyStrip = () => {
  const days = useMemo(() => currentCycleDays(new Date()), []);
  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`;
  }, []);
  const nxt = useMemo(() => nextTransition(new Date()), []);
  const todayParent = useMemo(() => custodyFor(new Date()), []);

  const daysCopy = (() => {
    if (!nxt) return null;
    if (nxt.daysUntil === 1) return 'tomorrow';
    if (nxt.daysUntil === 2) return 'in 2 days';
    return `in ${nxt.daysUntil} days`;
  })();

  const week1 = days.slice(0, 7);
  const week2 = days.slice(7, 14);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5" data-testid="nexa-custody-strip">
      <div className="flex items-center gap-2 mb-3">
        {todayParent === 'me'
          ? <Baby className="w-4 h-4 text-sky-400" strokeWidth={1.6} />
          : <Home className="w-4 h-4 text-white/60" strokeWidth={1.6} />}
        <span className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">Custody schedule</span>
      </div>
      <div className="mb-4">
        <p className="text-white text-lg font-light nexa-heading leading-snug">
          {todayParent === 'me'
            ? <>Today the kids are <span className="text-sky-300">with you</span>.</>
            : <>Today the kids are with <span className="text-white/80">{PARENT_LABEL.nigar}</span>.</>}
        </p>
        {daysCopy && (
          <p className="text-white/50 text-xs mt-1">
            {todayParent === 'me'
              ? `Switch to ${PARENT_LABEL.nigar}'s ${daysCopy}.`
              : `Back to you ${daysCopy}.`}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3">
        <CustodyRow label="This week" row={week1} todayKey={todayKey} />
        <CustodyRow label="Next week" row={week2} todayKey={todayKey} />
      </div>
      <div className="flex items-center gap-4 mt-4 text-[10px] font-mono tracking-widest uppercase text-white/50">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-sky-400" /> With you
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white/40" /> With {PARENT_LABEL.nigar}
        </span>
      </div>
    </div>
  );
};

// ---------- Kids list (grid of profile tiles) ----------

const KidTile = ({ kid, onOpen }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={() => onOpen(kid)}
    data-testid={`nexa-kid-tile-${kid.id}`}
    className="flex flex-col gap-2 p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-[#101014] to-[#0A0A0A] hover:border-sky-400/40 transition-colors text-left"
  >
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-sky-500/15 border border-sky-400/25 grid place-items-center">
        <Baby className="w-5 h-5 text-sky-300" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-lg font-medium tracking-tight">{kid.name}</div>
        <div className="text-[11px] font-mono tracking-widest uppercase text-white/40">
          {kid.age}y · {kid.gender || 'child'}
        </div>
      </div>
    </div>
    {kid.personality && (
      <p className="text-[12.5px] text-white/60 leading-snug line-clamp-3">{kid.personality}</p>
    )}
    {(kid.focus_areas || []).length > 0 && (
      <div className="flex flex-wrap gap-1.5 pt-1">
        {(kid.focus_areas || []).slice(0, 4).map((f) => (
          <span key={f.id} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">
            {f.label}
          </span>
        ))}
      </div>
    )}
  </motion.button>
);

// ---------- Single-kid detail ----------

const FactsPanel = ({ kidId, title }) => {
  const [facts, setFacts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const load = () =>
    nexaApi.get(`/kids/${kidId}/facts`).then(({ data }) => setFacts(data.facts));
  useEffect(() => { load(); }, [kidId]);

  const add = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await nexaApi.post('/kids/facts', {
        kid_id: kidId === 'general' ? null : kidId,
        content,
      });
      setDraft(''); setAdding(false); load();
    } catch (e) {
      toast.error('Could not save fact');
    }
  };
  const remove = async (id) => {
    if (!confirm('Delete this fact?')) return;
    try { await nexaApi.delete(`/kids/facts/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid={`nexa-kid-facts-${kidId}`}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/70 uppercase">
            {title || 'Facts NEXA remembers'}
          </div>
          <div className="text-[11px] text-white/40 mt-0.5">{facts.length} saved</div>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          data-testid={`nexa-kid-fact-add-${kidId}`}
          className="w-8 h-8 rounded-full border border-white/10 grid place-items-center text-sky-300 hover:border-sky-400/40 active:scale-95 transition-colors"
        >
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
      <AnimatePresence>
        {adding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="px-5 pb-3 flex flex-col gap-2"
          >
            <textarea
              value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
              placeholder="One line NEXA should remember…"
              data-testid={`nexa-kid-fact-input-${kidId}`}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50 resize-none"
            />
            <button
              onClick={add}
              className="self-end px-4 py-1.5 text-[12px] bg-sky-500/20 border border-sky-400/40 rounded-lg text-sky-200 hover:bg-sky-500/30"
              data-testid={`nexa-kid-fact-save-${kidId}`}
            >
              Save fact
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-h-80 overflow-y-auto">
        {facts.length === 0 ? (
          <p className="px-5 pb-4 text-[12.5px] text-white/40">No facts yet. Tap + to add one, or just talk to NEXA and she&apos;ll save them for you.</p>
        ) : facts.map((f) => (
          <div key={f.id} className="group flex items-start gap-3 px-5 py-2.5 border-t border-white/5">
            <span className="mt-1 text-[10px] font-mono tracking-widest uppercase text-white/30 shrink-0 min-w-[60px]">{f.tag || 'general'}</span>
            <p className="flex-1 text-[13px] text-white/80 leading-snug">{f.content}</p>
            <button
              onClick={() => remove(f.id)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-white/40 hover:text-rose-400 transition-opacity shrink-0"
              data-testid={`nexa-kid-fact-remove-${f.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const FocusPanel = ({ kid, onLog }) => {
  if (!(kid.focus_areas || []).length) return null;
  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0A]" data-testid={`nexa-kid-focus-${kid.id}`}>
      <div className="px-5 pt-4 pb-2 text-[10px] font-mono tracking-[0.3em] text-sky-400/70 uppercase">
        Current focus
      </div>
      <div className="px-5 pb-4 flex flex-col gap-2">
        {(kid.focus_areas || []).sort((a,b) => a.priority - b.priority).map((f) => {
          const Icon = FOCUS_ICONS[f.id] || Sparkles;
          const logKind = f.id === 'hebrew' ? 'hebrew_session'
            : f.id === 'math' ? 'math_session'
            : f.id === 'responsibility' ? 'responsibility_done'
            : f.id === 'social' ? 'social_moment' : null;
          return (
            <div key={f.id} className="flex items-start gap-3 py-2 border-t border-white/5 first:border-t-0">
              <div className="mt-0.5 w-8 h-8 rounded-lg border border-white/10 grid place-items-center text-sky-300 shrink-0">
                <Icon className="w-4 h-4" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] text-white/90">{f.label}</span>
                  {f.target && <span className="text-[10.5px] text-white/40 font-mono">{f.target}</span>}
                </div>
                {f.note && <p className="text-[11.5px] text-white/50 mt-0.5 leading-snug">{f.note}</p>}
              </div>
              {logKind && (
                <button
                  onClick={() => onLog(logKind, `${f.label} — session logged`)}
                  data-testid={`nexa-kid-focus-log-${kid.id}-${f.id}`}
                  className="px-2.5 py-1 rounded-lg text-[10.5px] bg-sky-500/15 border border-sky-400/30 text-sky-200 hover:bg-sky-500/25 shrink-0"
                >
                  + log
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const LOG_QUICK = [
  { kind: 'small_win', label: 'Small win' },
  { kind: 'struggle', label: 'Struggle' },
  { kind: 'social_moment', label: 'Social moment' },
  { kind: 'movie_watched', label: 'Movie' },
  { kind: 'mission_done', label: 'Mission' },
  { kind: 'hebrew_session', label: 'Hebrew' },
  { kind: 'math_session', label: 'Math' },
  { kind: 'responsibility_done', label: 'Chore' },
  { kind: 'note', label: 'Other' },
];

const LogPanel = ({ kidId, onStruggle }) => {
  const [entries, setEntries] = useState([]);
  const [openKind, setOpenKind] = useState(null);
  const [note, setNote] = useState('');

  const load = () =>
    nexaApi.get(`/kids/${kidId}/log`, { params: { days: 60 } }).then(({ data }) => setEntries(data.entries));
  useEffect(() => { load(); }, [kidId]);

  const submit = async (kind, noteText = '') => {
    try {
      await nexaApi.post('/kids/log', { kid_id: kidId, kind, note: noteText });
      setOpenKind(null); setNote(''); load();
      if (kind === 'struggle') onStruggle?.();
    } catch { toast.error('Log failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this entry?')) return;
    try { await nexaApi.delete(`/kids/log/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0A]" data-testid={`nexa-kid-log-${kidId}`}>
      <div className="px-5 pt-4 pb-3">
        <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/70 uppercase">Log book · last 60 days</div>
        <div className="text-[11px] text-white/40 mt-0.5">{entries.length} entries</div>
      </div>
      <div className="px-4 pb-3 flex flex-wrap gap-1.5">
        {LOG_QUICK.map((q) => (
          <button
            key={q.kind}
            onClick={() => setOpenKind(openKind === q.kind ? null : q.kind)}
            data-testid={`nexa-kid-log-quick-${q.kind}`}
            className={`px-3 py-1.5 rounded-full text-[11.5px] border transition-colors ${openKind === q.kind ? 'bg-sky-500/25 border-sky-400/50 text-sky-100' : 'bg-white/5 border-white/10 text-white/70 hover:border-white/25'}`}
          >
            + {q.label}
          </button>
        ))}
      </div>
      <AnimatePresence>
        {openKind && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-3 flex gap-2"
          >
            <input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note…"
              data-testid={`nexa-kid-log-note-${openKind}`}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sky-400/50"
              onKeyDown={(e) => e.key === 'Enter' && submit(openKind, note)}
            />
            <button
              onClick={() => submit(openKind, note)}
              className="px-3 py-1.5 rounded-lg text-[12px] bg-sky-500/25 border border-sky-400/40 text-sky-100"
              data-testid={`nexa-kid-log-submit-${openKind}`}
            >
              Save
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-h-80 overflow-y-auto">
        {entries.length === 0 ? (
          <p className="px-5 pb-4 text-[12.5px] text-white/40">Nothing logged yet.</p>
        ) : entries.map((e) => {
          const cfg = LOG_ICONS[e.kind] || LOG_ICONS.note;
          const Icon = cfg.icon;
          return (
            <div key={e.id} className="group flex items-start gap-3 px-5 py-2.5 border-t border-white/5">
              <div className={`w-8 h-8 rounded-lg border grid place-items-center shrink-0 ${cfg.tone}`}>
                <Icon className="w-4 h-4" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12.5px] text-white/90 capitalize">{e.kind.replace(/_/g, ' ')}</span>
                  <span className="text-[10.5px] text-white/40 font-mono">{timeAgo(e.ts)}</span>
                </div>
                {e.note && <p className="text-[12px] text-white/60 mt-0.5 leading-snug">{e.note}</p>}
              </div>
              <button
                onClick={() => remove(e.id)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-white/40 hover:text-rose-400 transition-opacity shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const MoviesPanel = () => {
  const [movies, setMovies] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  const load = () => nexaApi.get('/kids/movies').then(({ data }) => setMovies(data.movies));
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    try { await nexaApi.post(`/kids/movies/${id}/toggle`); load(); } catch { toast.error('Failed'); }
  };
  const add = async () => {
    const t = title.trim();
    if (!t) return;
    try { await nexaApi.post('/kids/movies', { title: t }); setTitle(''); setAdding(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this movie?')) return;
    try { await nexaApi.delete(`/kids/movies/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0A]" data-testid="nexa-kid-movies">
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/70 uppercase">
            Movies for social learning
          </div>
          <div className="text-[11px] text-white/40 mt-0.5">
            {movies.filter((m) => m.watched).length}/{movies.length} watched
          </div>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          data-testid="nexa-kid-movie-add"
          className="w-8 h-8 rounded-full border border-white/10 grid place-items-center text-sky-300 hover:border-sky-400/40 active:scale-95"
        >
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
      <AnimatePresence>
        {adding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="px-5 pb-3 flex gap-2"
          >
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Movie title…" onKeyDown={(e) => e.key === 'Enter' && add()}
              data-testid="nexa-kid-movie-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sky-400/50"
            />
            <button onClick={add} className="px-3 py-1.5 rounded-lg text-[12px] bg-sky-500/25 border border-sky-400/40 text-sky-100">Add</button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-h-80 overflow-y-auto">
        {movies.map((m) => (
          <div key={m.id} className="group flex items-center gap-3 px-5 py-2.5 border-t border-white/5">
            <button
              onClick={() => toggle(m.id)}
              data-testid={`nexa-kid-movie-toggle-${m.id}`}
              className={`w-6 h-6 rounded-md border grid place-items-center shrink-0 ${m.watched ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-200' : 'border-white/20 text-transparent hover:border-white/40'}`}
            >
              <Check className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] ${m.watched ? 'text-white/40 line-through' : 'text-white/85'}`}>{m.title}</div>
              {m.theme && <div className="text-[10.5px] text-white/40 font-mono">{m.theme}</div>}
            </div>
            <button
              onClick={() => remove(m.id)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-white/30 hover:text-rose-400"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const MissionsPanel = ({ kidId }) => {
  const [missions, setMissions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  const load = () =>
    nexaApi.get('/kids/missions').then(({ data }) =>
      setMissions(data.missions.filter((m) => m.kid_id === kidId)));
  useEffect(() => { load(); }, [kidId]);

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    try { await nexaApi.post('/kids/missions', { kid_id: kidId, title: t }); setTitle(''); setAdding(false); load(); }
    catch { toast.error('Failed'); }
  };
  const toggle = async (id) => {
    try { await nexaApi.post(`/kids/missions/${id}/toggle`); load(); } catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this mission?')) return;
    try { await nexaApi.delete(`/kids/missions/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0A]" data-testid={`nexa-kid-missions-${kidId}`}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/70 uppercase">
            Dad-and-kid missions
          </div>
          <div className="text-[11px] text-white/40 mt-0.5">Shared experiences, not lessons</div>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          data-testid={`nexa-kid-mission-add-${kidId}`}
          className="w-8 h-8 rounded-full border border-white/10 grid place-items-center text-sky-300 hover:border-sky-400/40 active:scale-95"
        >
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
      <AnimatePresence>
        {adding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="px-5 pb-3 flex gap-2"
          >
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Make a YouTube video together" onKeyDown={(e) => e.key === 'Enter' && add()}
              data-testid={`nexa-kid-mission-input-${kidId}`}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sky-400/50"
            />
            <button onClick={add} className="px-3 py-1.5 rounded-lg text-[12px] bg-sky-500/25 border border-sky-400/40 text-sky-100">Add</button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-h-80 overflow-y-auto">
        {missions.length === 0 ? (
          <p className="px-5 pb-4 text-[12.5px] text-white/40">No missions yet. Add a shared thing to do — make a video, VR session, cook dinner together.</p>
        ) : missions.map((m) => (
          <div key={m.id} className="group flex items-center gap-3 px-5 py-2.5 border-t border-white/5">
            <button
              onClick={() => toggle(m.id)}
              className={`w-6 h-6 rounded-md border grid place-items-center shrink-0 ${m.done ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-200' : 'border-white/20 text-transparent hover:border-white/40'}`}
            >
              <Check className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <div className={`flex-1 text-[13px] ${m.done ? 'text-white/40 line-through' : 'text-white/85'}`}>{m.title}</div>
            <button
              onClick={() => remove(m.id)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-white/30 hover:text-rose-400"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const REFRAME_TEXT = "Is this something he genuinely needs right now, or are you uncomfortable because he isn't progressing as quickly as you would?";

const KidDetail = ({ kid, onBack }) => {
  const [showReframe, setShowReframe] = useState(false);
  const onStruggle = () => {
    setShowReframe(true);
    setTimeout(() => setShowReframe(false), 12000);
  };
  return (
    <motion.div
      key={kid.id}
      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25 }} className="flex flex-col gap-5"
    >
      <button
        onClick={onBack}
        data-testid="nexa-kid-back"
        className="self-start flex items-center gap-1.5 text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 hover:text-sky-400"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Kids
      </button>
      <div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-sky-500/15 border border-sky-400/25 grid place-items-center">
            <Baby className="w-6 h-6 text-sky-300" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="nexa-heading text-3xl font-light tracking-tight">{kid.name}</h1>
            <div className="text-[11px] font-mono tracking-widest uppercase text-white/40">
              {kid.age} years old · {kid.gender || 'child'}
            </div>
          </div>
        </div>
        {kid.personality && (
          <p className="mt-3 text-[13.5px] text-white/70 leading-relaxed">{kid.personality}</p>
        )}
      </div>

      <AnimatePresence>
        {showReframe && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-5 py-4 flex gap-3"
            data-testid="nexa-kid-reframe"
          >
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] font-mono tracking-[0.3em] text-amber-300 uppercase">Pause · Reframe</div>
              <p className="text-[13px] text-amber-100/90 mt-1 leading-relaxed">{REFRAME_TEXT}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FocusPanel kid={kid} onLog={async (kind, note) => {
        try { await nexaApi.post('/kids/log', { kid_id: kid.id, kind, note }); toast.success('Logged.'); }
        catch { toast.error('Log failed'); }
      }} />

      <FactsPanel kidId={kid.id} title={`Facts NEXA remembers about ${kid.name}`} />

      <LogPanel kidId={kid.id} onStruggle={onStruggle} />

      {kid.id === 'dan' && <MoviesPanel />}

      <MissionsPanel kidId={kid.id} />
    </motion.div>
  );
};

// ---------- Exported top-level ----------

export const KidsDetail = () => {
  const [kids, setKids] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    nexaApi.get('/kids').then(({ data }) => setKids(data.kids)).catch(() => {});
  }, []);

  const dayOfWeek = useMemo(() => new Date().getDay(), []);

  if (selected) {
    const kid = kids.find((k) => k.id === selected);
    if (!kid) { setSelected(null); return null; }
    return <KidDetail kid={kid} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="flex flex-col gap-5" data-testid="nexa-kids-view">
      <CustodyStrip />

      <div className="rounded-2xl border border-sky-400/20 bg-gradient-to-br from-sky-500/10 to-transparent px-5 py-4">
        <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/80 uppercase">Guiding principle</div>
        <p className="text-[14px] text-white/85 mt-1 leading-relaxed">
          Connection first. Growth second. Small consistent steps instead of pressure.
        </p>
        {dayOfWeek === 0 && (
          <p className="text-[12.5px] text-sky-200/80 mt-2 italic">
            Sunday reflection · &ldquo;Did I help him enjoy improving this week?&rdquo; — not &ldquo;did he improve enough?&rdquo;
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {kids.map((k) => <KidTile key={k.id} kid={k} onOpen={(kid) => setSelected(kid.id)} />)}
      </div>

      <p className="text-[11px] text-white/30 text-center px-6 font-mono tracking-wider">
        Parenting principles NEXA holds live in the <span className="text-sky-300/80">Memory</span> tab, under &ldquo;kids&rdquo;.
      </p>
    </div>
  );
};

export default KidsDetail;
